import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import moment from "moment-timezone";

import Button from "ui/Button";
import CopyCodeBlock from "ui/CopyCodeBlock";
import Grid, { GridPageControls, PageHeightWrapper } from "ui/Grid";
import { ExpandToggle } from "ui/Icons";
import PageSecondaryNavigation, {
  PageNavLink,
} from "ui/PageSecondaryNavigation";
import SectionHeading from "ui/SectionHeading";
import Tip from "ui/Tip";
import {
  formatTimeAgo,
  formatTimestampLong,
  formatTimestampShortest,
} from "utils/format";
import { useRoutes } from "utils/routes";

import { TimeAgo } from "../../EndpointHealth";
import {
  IncidentIoDeliveryFieldsFragment,
  WebhookDeliveryFieldsFragment,
} from "./gql/Fragment.generated";
import {
  useIncidentIoDeliveriesQuery,
  useWebhookDeliveriesQuery,
} from "./gql/Query.generated";

// The kinds of endpoint the webhook pipeline delivers to
export type EndpointKind = "webhook" | "incident_io";

export type DeliveryType =
  | WebhookDeliveryFieldsFragment
  | IncidentIoDeliveryFieldsFragment;

export const ENDPOINT_NOUNS: Record<EndpointKind, string> = {
  webhook: "webhook",
  incident_io: "alert source",
};

type StatusFilter = "all" | "failed" | "in-progress" | "delivered";

const STATUS_FILTER_OPTS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Failed", value: "failed" },
  { label: "In progress", value: "in-progress" },
  { label: "Delivered", value: "delivered" },
];

const FAILED_STATUSES = ["failed", "exhausted", "dropped"];

const FILTER_STATUSES: Record<StatusFilter, string[] | undefined> = {
  all: undefined,
  failed: FAILED_STATUSES,
  "in-progress": ["pending", "retrying"],
  delivered: ["succeeded"],
};

const ERROR_KIND_LABELS: Record<string, string> = {
  timeout: "Timed out",
  connection: "Connection failed",
  tls: "TLS error",
  blocked_destination: "Blocked destination",
  http_status: "HTTP error",
  internal: "Internal error",
};

const PAGE_SIZE = 10;

// Must match Webhooks::CleanupWorker::DELIVERY_RETENTION
const DELIVERY_RETENTION_DESCRIPTION = "30 days";

export function DeliveryLog({
  organizationSlug,
  kind,
  endpointId,
  onSelect,
}: {
  organizationSlug: string;
  kind: EndpointKind;
  endpointId: string;
  onSelect: (delivery: DeliveryType) => void;
}) {
  const {
    organizationIntegrationsWebhook,
    organizationIntegrationsIncidentIo,
  } = useRoutes();
  const endpointPath =
    kind === "webhook"
      ? organizationIntegrationsWebhook
      : organizationIntegrationsIncidentIo;
  const filter = useStatusFilter();
  return (
    <>
      <SectionHeading className="mt-2">
        Deliveries{" "}
        <Tip content="Test requests are not logged. Deliveries of alerts for servers you don't have access to are not shown." />
      </SectionHeading>
      <PageSecondaryNavigation variant="section">
        {STATUS_FILTER_OPTS.map((opt) => (
          <PageNavLink
            key={opt.value}
            to={endpointPath(
              organizationSlug,
              endpointId,
              opt.value === "all" ? undefined : opt.value,
            )}
            active={opt.value === filter}
          >
            {opt.label}
          </PageNavLink>
        ))}
      </PageSecondaryNavigation>
      {/* Remount on filter change to start paging from the top */}
      <DeliveryList
        key={filter}
        kind={kind}
        endpointId={endpointId}
        filter={filter}
        onSelect={onSelect}
      />
    </>
  );
}

function useStatusFilter(): StatusFilter {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  return STATUS_FILTER_OPTS.find((opt) => opt.value === status)?.value ?? "all";
}

function DeliveryList({
  kind,
  endpointId,
  filter,
  onSelect,
}: {
  kind: EndpointKind;
  endpointId: string;
  filter: StatusFilter;
  onSelect: (delivery: DeliveryType) => void;
}) {
  const {
    deliveries,
    loading,
    error,
    page,
    paginated,
    pageLabel,
    prevDisabled,
    nextDisabled,
    showPrev,
    showNext,
  } = useDeliveryPage(kind, endpointId, FILTER_STATUSES[filter]);

  return (
    <>
      <PageHeightWrapper isPaginated={paginated} page={page}>
        <Grid
          data={deliveries}
          loading={loading}
          error={!!error}
          sortParam={false}
          noRowsText={
            filter === "all"
              ? `No alerts were sent to this ${ENDPOINT_NOUNS[kind]} in the last ${DELIVERY_RETENTION_DESCRIPTION}.`
              : "No deliveries match this filter."
          }
          columns={[
            {
              field: "createdAt",
              header: "Time",
              width: "145px",
              disableSort: true,
              title: function TimeTitle({ fieldData }) {
                return formatTimestampLong(moment.unix(fieldData));
              },
              renderer: function TimeCell({ rowData }) {
                return (
                  <Button bare variant="link" onClick={() => onSelect(rowData)}>
                    {formatTimestampShortest(moment.unix(rowData.createdAt))}
                  </Button>
                );
              },
            },
            {
              field: "eventType",
              header: "Event",
              width: "175px",
              style: "query",
              disableSort: true,
            },
            {
              field: "issue",
              header: "Issue",
              width: "minmax(200px, 1fr)",
              disableSort: true,
              nullValue: "—",
              title: function IssueTitle({ rowData }) {
                return rowData.issue?.description ?? "";
              },
              renderer: IssueCell,
            },
            {
              field: "status",
              header: "Status",
              width: "220px",
              disableSort: true,
              renderer: function StatusCell({ rowData }) {
                return <DeliveryStatus kind={kind} delivery={rowData} />;
              },
            },
            {
              field: "responseStatus",
              header: "Response",
              width: "100px",
              disableSort: true,
              title: function ResponseTitle({ rowData }) {
                return rowData.errorMessage ?? "";
              },
              renderer: function ResponseCell({ rowData }) {
                return responseSummary(rowData) ?? "—";
              },
            },
          ]}
        />
      </PageHeightWrapper>
      {paginated && (
        <div className="flex pt-[8px] items-center justify-end">
          <GridPageControls
            label={pageLabel}
            prevDisabled={prevDisabled}
            nextDisabled={nextDisabled}
            onPrev={showPrev}
            onNext={showNext}
          />
        </div>
      )}
    </>
  );
}

function useDeliveryPage(
  kind: EndpointKind,
  endpointId: string,
  statuses?: string[],
) {
  const [page, setPage] = useState(0);
  const variables = {
    statuses,
    limit: PAGE_SIZE + 1,
    offset: page * PAGE_SIZE,
  };
  const webhookQuery = useWebhookDeliveriesQuery({
    variables: { webhookEndpointId: endpointId, ...variables },
    skip: kind !== "webhook",
  });
  const incidentIoQuery = useIncidentIoDeliveriesQuery({
    variables: { incidentIoEndpointId: endpointId, ...variables },
    skip: kind !== "incident_io",
  });
  const { data, previousData, loading, error } =
    kind === "webhook" ? webhookQuery : incidentIoQuery;
  // Keep showing the current page while the next one loads
  const rows: DeliveryType[] =
    kind === "webhook"
      ? (webhookQuery.data ?? webhookQuery.previousData)
          ?.getWebhookDeliveries ?? []
      : (incidentIoQuery.data ?? incidentIoQuery.previousData)
          ?.getIncidentIoDeliveries ?? [];
  const deliveries = rows.slice(0, PAGE_SIZE);
  const firstItem = page * PAGE_SIZE + 1;

  return {
    deliveries,
    loading: loading && !data && !previousData,
    error,
    page,
    paginated: page > 0 || rows.length > PAGE_SIZE,
    pageLabel: `${firstItem}-${firstItem + deliveries.length - 1}`,
    prevDisabled: page === 0 || loading,
    nextDisabled: rows.length <= PAGE_SIZE || loading,
    showPrev: () => setPage((p) => p - 1),
    showNext: () => setPage((p) => p + 1),
  };
}

function IssueCell({ rowData }: { rowData: DeliveryType }) {
  const { databaseIssue, serverIssue } = useRoutes();
  const { issue } = rowData;
  const url = issue.database
    ? databaseIssue(issue.database.id, issue.id, issue.checkGroupAndName)
    : serverIssue(issue.server.humanId, issue.id, issue.checkGroupAndName);
  return (
    <>
      <Link to={url}>{issue.description}</Link>
      <span className="text-[#606771]"> · {issue.server.name}</span>
    </>
  );
}

export function DeliveryDetails({
  kind,
  delivery,
  actions,
}: {
  kind: EndpointKind;
  delivery: DeliveryType;
  actions?: React.ReactNode;
}) {
  const response = responseSummary(delivery);
  return (
    <>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
        <dt>Event</dt>
        <dd className="mb-0 font-query">{delivery.eventType}</dd>
        {"eventId" in delivery ? (
          <>
            <dt>Event ID</dt>
            <dd className="mb-0 break-all font-query">{delivery.eventId}</dd>
          </>
        ) : (
          <>
            <dt>Deduplication key</dt>
            <dd className="mb-0 break-all font-query">
              {delivery.deduplicationKey}
            </dd>
          </>
        )}
        <dt>Created</dt>
        <dd className="mb-0">
          {formatTimestampLong(moment.unix(delivery.createdAt))}
        </dd>
        {delivery.issue && (
          <>
            <dt>Issue</dt>
            <dd className="mb-0">
              <IssueCell rowData={delivery} />
            </dd>
          </>
        )}
        <dt>Status</dt>
        <dd className="mb-0">
          <DeliveryStatus kind={kind} delivery={delivery} />
        </dd>
        <dt>Attempts</dt>
        <dd className="mb-0">{delivery.attempts}</dd>
        {delivery.lastAttemptAt != null && (
          <>
            <dt>Last attempt</dt>
            <dd className="mb-0">
              <TimestampWithRelative ts={delivery.lastAttemptAt} />
            </dd>
          </>
        )}
        {delivery.nextAttemptAt != null && (
          <>
            <dt>Next attempt</dt>
            <dd className="mb-0">
              <TimestampWithRelative ts={delivery.nextAttemptAt} />
            </dd>
          </>
        )}
        {response && (
          <>
            <dt>Response</dt>
            <dd className="mb-0">{response}</dd>
          </>
        )}
        {delivery.errorMessage && delivery.errorMessage !== response && (
          <>
            <dt>Error</dt>
            <dd className="mb-0 break-words">{delivery.errorMessage}</dd>
          </>
        )}
      </dl>
      {actions}
      {delivery.responseBodyExcerpt && (
        <div className="mt-4">
          <SectionHeading>Response body (excerpt)</SectionHeading>
          <CopyCodeBlock code={delivery.responseBodyExcerpt} />
        </div>
      )}
      <ExpandablePayload payload={delivery.payload} />
    </>
  );
}

function ExpandablePayload({ payload }: { payload: unknown }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-4">
      <SectionHeading>
        <Button
          bare
          aria-expanded={expanded}
          className="uppercase tracking-wide"
          onClick={() => setExpanded(!expanded)}
        >
          <ExpandToggle className="mr-2 w-3" expanded={expanded} />
          Payload
        </Button>
      </SectionHeading>
      {expanded && (
        <CopyCodeBlock
          language="json"
          code={JSON.stringify(payload, null, 2)}
        />
      )}
    </div>
  );
}

function TimestampWithRelative({ ts }: { ts: number }) {
  const time = moment.unix(ts);
  return (
    <>
      {formatTimestampLong(time)}{" "}
      <span className="text-[#606771]">({formatTimeAgo(time)})</span>
    </>
  );
}

function DeliveryStatus({
  kind,
  delivery,
}: {
  kind: EndpointKind;
  delivery: DeliveryType;
}) {
  switch (delivery.status) {
    case "succeeded":
      return <span className="text-[#398439]">Delivered</span>;
    case "pending":
      return <span className="text-[#606771]">Pending</span>;
    case "retrying":
      return (
        <span className="text-[#606771]">
          Retrying
          {delivery.nextAttemptAt != null &&
            delivery.nextAttemptAt > moment().unix() && (
              <>
                {" "}
                <TimeAgo ts={delivery.nextAttemptAt} />
              </>
            )}
        </span>
      );
    case "failed":
      return <span className="text-[#c93a2a]">Failed</span>;
    case "exhausted":
      return (
        <span className="text-[#c93a2a]">
          Failed after {delivery.attempts} attempts
        </span>
      );
    case "dropped":
      return (
        <span
          className="text-[#606771]"
          title={`The ${ENDPOINT_NOUNS[kind]} was disabled before this alert was delivered`}
        >
          Not sent ({ENDPOINT_NOUNS[kind]} disabled)
        </span>
      );
    case "superseded":
      return (
        <span
          className="text-[#606771]"
          title="A newer alert for this issue was sent instead"
        >
          Not sent (superseded)
        </span>
      );
    default:
      return <>{delivery.status}</>;
  }
}

function responseSummary(delivery: DeliveryType) {
  if (delivery.responseStatus != null) {
    return `HTTP ${delivery.responseStatus}`;
  }
  if (delivery.errorKind != null) {
    return ERROR_KIND_LABELS[delivery.errorKind] ?? delivery.errorKind;
  }
  return null;
}

// Finished without being delivered or waiting for a retry
export function isRetryable(delivery: DeliveryType) {
  return (
    FAILED_STATUSES.includes(delivery.status) ||
    (delivery.status === "retrying" &&
      delivery.nextAttemptAt != null &&
      delivery.nextAttemptAt > moment().unix())
  );
}
